import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  TablePagination,
  IconButton,
  Chip,
} from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import type { CheckInAuditEvent } from '../types';
import type { PagedResult } from '../types/pagedResult';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import ClearIcon from '@mui/icons-material/Clear';
import { formatDate } from '../utils/dateFormat';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad, pagination, permissionCopy, displayTimeZoneCopy } from '../utils/userFacingCopy';

export default function CheckInAuditLogPage() {
  const { user, displayTimeZoneId } = useAuth();
  const isAdmin = user?.roles.includes('Administrator') ?? false;
  const [auditEvents, setAuditEvents] = useState<CheckInAuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pagination.auditLogDefaultPageSize);
  const [totalCount, setTotalCount] = useState(0);

  // Debounce search to avoid too many API calls
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
    }, 400);

    return () => window.clearTimeout(handle);
  }, [searchQuery]);

  const loadAuditEvents = useCallback(async () => {
    if (!isAdmin) {
      setError(permissionCopy.administrator);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      params.append('page', (page + 1).toString()); // API uses 1-based pages
      params.append('pageSize', rowsPerPage.toString());
      if (debouncedSearchQuery.trim()) {
        params.append('searchQuery', debouncedSearchQuery.trim());
      }
      if (eventTypeFilter && eventTypeFilter !== 'all') {
        params.append('eventTypeFilter', eventTypeFilter);
      }

      const response = await apiService.get<PagedResult<CheckInAuditEvent>>(
        `${API_ENDPOINTS.CHECK_INS.AUDIT_LOG}?${params.toString()}`
      );
      setAuditEvents(response.data.items);
      setTotalCount(response.data.totalCount);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('audit events')));
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, rowsPerPage, debouncedSearchQuery, eventTypeFilter]);

  useEffect(() => {
    loadAuditEvents();
  }, [loadAuditEvents]);

  const getEventTypeLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      ConfigCreated: 'Config Created',
      ConfigUpdated: 'Config Updated',
      ConfigDeactivated: 'Config Deactivated',
      CheckInScheduled: 'Check-in Scheduled',
      ReminderSent: 'Reminder Sent',
      ReminderSentUrgent: 'Urgent Reminder Sent',
      ManagerConfirmed: 'Manager Confirmed',
      EmployeeConfirmed: 'Employee Confirmed',
      CheckInCancelledDueToFrequencyRemoved: 'Check-in cancelled (frequency removed)',
      CheckInCancelledDueToManagerOptOut: 'Check-in cancelled (manager opted out)',
    };
    return labels[eventType] || eventType;
  };

  const eventTypes = [
    'all',
    'ConfigCreated',
    'ConfigUpdated',
    'ConfigDeactivated',
    'CheckInScheduled',
    'CheckInCancelledDueToFrequencyRemoved',
    'CheckInCancelledDueToManagerOptOut',
    'ReminderSent',
    'ReminderSentUrgent',
    'ManagerConfirmed',
    'EmployeeConfirmed',
  ];

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(0); // Reset to first page on search
  };

  const handleEventTypeFilterChange = (value: string) => {
    setEventTypeFilter(value);
    setPage(0); // Reset to first page on filter change
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setPage(0);
  };

  if (!isAdmin) {
    return (
      <AppLayout>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Alert severity="error">{permissionCopy.administrator}</Alert>
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <HistoryIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
          <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
            Check-In Audit Log
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          This page shows all check-in audit events across the system for compliance reporting.{' '}
          {displayTimeZoneCopy.auditLogTimesHint}
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Search audit events"
                placeholder="Search by user, actor, or details..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        edge="end"
                        sx={{ mr: -1 }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={eventTypeFilter}
                  label="Event Type"
                  onChange={(e) => handleEventTypeFilterChange(e.target.value)}
                >
                  {eventTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type === 'all' ? 'All Events' : getEventTypeLabel(type)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {(searchQuery.trim() !== '' || eventTypeFilter !== 'all') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchQuery.trim() !== '' && (
                  <Chip
                    label={`Search: "${searchQuery}"`}
                    onDelete={handleClearSearch}
                    size="small"
                    color="primary"
                  />
                )}
                {eventTypeFilter !== 'all' && (
                  <Chip
                    label={`Event Type: ${getEventTypeLabel(eventTypeFilter)}`}
                    onDelete={() => handleEventTypeFilterChange('all')}
                    size="small"
                    color="info"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined">
                  <Table key={displayTimeZoneId}>
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Timestamp</strong></TableCell>
                        <TableCell><strong>Event Type</strong></TableCell>
                        <TableCell><strong>Actor</strong></TableCell>
                        <TableCell><strong>Details</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {auditEvents.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 0, border: 'none' }}>
                            <Box sx={{ py: 4 }}>
                              <HistoryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                              <Typography variant="h6" color="text.secondary" gutterBottom>
                                No Audit Events Found
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {searchQuery || eventTypeFilter !== 'all'
                                  ? 'Try adjusting your search or filter criteria to find what you\'re looking for.'
                                  : 'No check-in audit events have been recorded yet. Events will appear here as check-ins are configured, scheduled, and completed.'}
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        auditEvents.map((event) => (
                          <TableRow key={event.id}>
                            <TableCell>{formatDate(event.eventAt, { includeTime: true })}</TableCell>
                            <TableCell>{getEventTypeLabel(event.eventType)}</TableCell>
                            <TableCell>{event.actorName}</TableCell>
                            <TableCell>{event.details || '-'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[...pagination.auditLogPageSizeOptions]}
              />
            </>
        )}
      </Box>
    </AppLayout>
  );
}
